import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Megaphone, Star, Briefcase, CalendarDays, LayoutGrid, Home, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { SectionHeading } from "../components/Motifs";
import { useLang } from "../lib/i18n";

const AD_PRODUCTS = [
  { icon: LayoutGrid, tKey: "adp.p1.t", xKey: "adp.p1.x", tid: "banner-ads" },
  { icon: Star, tKey: "adp.p2.t", xKey: "adp.p2.x", tid: "sponsored-featured-listings" },
  { icon: Briefcase, tKey: "adp.p3.t", xKey: "adp.p3.x", tid: "sponsored-jobs" },
  { icon: CalendarDays, tKey: "adp.p4.t", xKey: "adp.p4.x", tid: "sponsored-events" },
  { icon: Home, tKey: "adp.p5.t", xKey: "adp.p5.x", tid: "category-promotions" },
  { icon: Megaphone, tKey: "adp.p6.t", xKey: "adp.p6.x", tid: "homepage-placements" },
];

const PLACEMENTS = ["homepage_top", "homepage_mid", "category_top", "search_results", "listing_sidebar"];

const AD_TYPES = ["banner", "sponsored_business", "featured_listing", "sponsored_job", "sponsored_event", "category_promotion", "homepage_placement"];

const EMPTY = {
  business_name: "", contact_name: "", email: "", phone: "", website: "",
  ad_type: "banner", placement: "homepage_top", category: "", country: "US",
  start_date: "", end_date: "", banner_url: "", budget: "", message: "",
};

export default function Advertise() {
  const { t, tOr } = useLang();
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/ad-requests", form);
      setSent(true);
      toast.success(t("toast.ad.ok"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl bg-sand/60 text-sm outline-none border border-transparent focus:border-gold";

  if (sent) {
    return (
      <div className="py-24 lg:py-32 bg-cream acheik-bg" data-testid="advertise-success">
        <div className="max-w-xl mx-auto px-4 text-center">
          <CheckCircle2 className="w-14 h-14 text-jade mx-auto mb-6" />
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-ink mb-4">{t("adp.success.t")}</h1>
          <p className="text-clay leading-relaxed mb-8">{t("adp.success.x")}</p>
          <Link to="/" className="bg-ruby text-cream text-sm font-semibold px-6 py-3 rounded-full" data-testid="advertise-success-home-btn">{t("adp.back")}</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="advertise-page">
      <section className="bg-teak acheik-bg-dark text-cream py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold mb-4">{t("adp.overline")}</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-testid="advertise-title">
            {t("adp.h1a")}<span className="font-serif italic text-gold">{t("adp.h1b")}</span>
          </h1>
          <p className="mt-4 text-cream/60 max-w-xl">{t("adp.intro")}</p>
        </div>
      </section>

      <section className="py-14 lg:py-20 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading overline={t("adp.products")} title={t("adp.ways")} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-16" data-testid="ad-products-grid">
            {AD_PRODUCTS.map((p) => (
              <div key={p.tid} className="bg-white border border-[#E6DED3] rounded-2xl p-6 hover:border-gold transition-colors" data-testid={`ad-product-${p.tid}`}>
                <p.icon className="w-6 h-6 text-gold-dark mb-4" />
                <p className="font-display font-bold text-ink">{t(p.tKey)}</p>
                <p className="text-sm text-clay mt-2 leading-relaxed">{t(p.xKey)}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="bg-teak text-cream rounded-2xl p-7 acheik-bg-dark lg:sticky lg:top-28" data-testid="billing-note-card">
                <Receipt className="w-7 h-7 text-gold mb-4" />
                <p className="font-display font-bold text-xl mb-3">{t("adp.billing.t")}</p>
                <p className="text-sm text-cream/70 leading-relaxed">{t("adp.billing.x")}</p>
                <div className="mt-6 pt-5 border-t border-teak-line space-y-2 text-xs text-cream/50">
                  <p>{t("adp.b1")}</p>
                  <p>{t("adp.b2")}</p>
                  <p>{t("adp.b3")}</p>
                </div>
              </div>
            </div>

            <form onSubmit={submit} className="lg:col-span-8 bg-white border border-[#E6DED3] rounded-2xl p-6 sm:p-10 space-y-6" data-testid="advertise-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input required value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder={t("adp.biz")} className={inputCls} data-testid="ad-business-name-input" />
                <input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder={t("adp.contactperson")} className={inputCls} data-testid="ad-contact-name-input" />
                <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder={t("adp.email")} className={inputCls} data-testid="ad-email-input" />
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder={t("adp.phone")} className={inputCls} data-testid="ad-phone-input" />
                <input value={form.website} onChange={(e) => set("website", e.target.value)} placeholder={t("adp.website")} className={`${inputCls} sm:col-span-2`} data-testid="ad-website-input" />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <select value={form.ad_type} onChange={(e) => set("ad_type", e.target.value)} className={inputCls} data-testid="ad-type-select">
                  {AD_TYPES.map((v) => <option key={v} value={v}>{t(`adtype.${v}`)}</option>)}
                </select>
                <select value={form.placement} onChange={(e) => set("placement", e.target.value)} className={inputCls} data-testid="ad-placement-select">
                  {PLACEMENTS.map((v) => <option key={v} value={v}>{t(`pl.${v}`)}</option>)}
                </select>
                <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="ad-category-select">
                  <option value="">{t("adp.allcat")}</option>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{tOr(`cat.${c.slug}`, c.label)}</option>)}
                </select>
                <input value={form.budget} onChange={(e) => set("budget", e.target.value)} placeholder={t("adp.budget")} className={inputCls} data-testid="ad-budget-input" />
                <div>
                  <label className="text-xs text-stone2 block mb-1.5">{t("adp.start")}</label>
                  <input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className={inputCls} data-testid="ad-start-date-input" />
                </div>
                <div>
                  <label className="text-xs text-stone2 block mb-1.5">{t("adp.end")}</label>
                  <input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className={inputCls} data-testid="ad-end-date-input" />
                </div>
              </div>

              <input value={form.banner_url} onChange={(e) => set("banner_url", e.target.value)} placeholder={t("adp.banner")} className={inputCls} data-testid="ad-banner-url-input" />
              <textarea value={form.message} onChange={(e) => set("message", e.target.value)} placeholder={t("adp.message")} rows={3} className={inputCls} data-testid="ad-message-input" />

              <button
                type="submit"
                disabled={busy}
                className="w-full bg-gold hover:bg-gold-dark disabled:opacity-60 text-ink font-bold py-4 rounded-full transition-colors shadow-[0_10px_30px_rgba(212,175,55,0.35)]"
                data-testid="ad-request-submit-btn"
              >
                {busy ? t("adp.sending") : t("adp.submit")}
              </button>
              <p className="text-center text-xs text-stone2">
                {t("form.agree")} <Link to="/privacy-policy" className="text-ruby font-semibold hover:text-gold-dark" data-testid="advertise-privacy-link">{t("footer.privacy")}</Link>{t("form.agree.end")}
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
